// Part groups --- BOM editor help (shared/part-groups.ts). Nothing here changes how a BOM resolves: bom_option_map stays
// the only truth, frozen with each revision. Fill from group… proposes, for a DRAFT option line, a member of a part group
// for each value of the line's option (the same value, else what the line maps now, else what other BOMs call it, else
// the line's own part, else "pick one"), shows the table and writes plain bom_option_map rows for what changes. A line
// that is "-LG for every colour but Ghost Black" on purpose comes out unchanged. Also: an option line's group label, and
// a chip on an end-of-life part with a current sibling of the same values (Replace part).
import { Boxes, Wand2 } from "lucide-react";
import { useMemo, useState } from "react";
import type { BomIndex, BomLineRow, BomRow } from "../../../shared/boms.ts";
import { conditionsOf } from "../../../shared/boms.ts";
import type { Item, PartGroup } from "../../../shared/db.gen.ts";
import {
  FILL_SOURCE_LABELS,
  type FillRow,
  fillProposal,
  fillWrite,
  isCurrent,
  lineGroup,
  lineValues,
  mapsElsewhere,
  memberLabel,
  type OwnMap,
  valuesKey,
  valuesOf,
} from "../../../shared/part-groups.ts";
import { plural } from "../lib/format.ts";
import { mutate } from "../lib/mutate.ts";
import { useRows } from "../lib/sync.ts";
import { Badge, Button, cx, Select } from "../ui/controls.tsx";
import { toast } from "../ui/toast.tsx";
import { Modal, usePartGroups } from "./PartGroups.tsx";

type Groups = ReturnType<typeof usePartGroups>;

/** The group an option line picks from (every part it picks in one group), for its label and Fill from group. */
export function lineGroupOf(ix: BomIndex, line: BomLineRow | undefined, pg: Groups): PartGroup | undefined {
  if (!line?.option_name) return undefined;
  const id = lineGroup(ix, line, (itemId) => (ix.itemById.get(itemId) as Item | undefined)?.part_group_id ?? null);
  return id ? pg.byId.get(id) : undefined;
}

/** "GCU2 box sleeve · Wireless": the group and the one value the line's conditions pin. */
export function lineGroupText(group: PartGroup, line: BomLineRow): string {
  const pinned = Object.values(conditionsOf(line))
    .filter((v) => v.length === 1)
    .map((v) => v[0]);
  return [group.name, ...pinned].join(" · ");
}

/** A chip naming an option line's group. */
export function LineGroupChip({ group, line }: { group: PartGroup; line: BomLineRow }) {
  return (
    <span
      className="inline-flex h-5 shrink-0 items-center gap-1 rounded bg-surface-2 px-1.5 text-[11px] text-muted"
      title={`Picks from ${group.name} by ${line.option_name}`}
    >
      <Boxes className="size-3" />
      {lineGroupText(group, line)}
    </span>
  );
}

/** An end-of-life part's current sibling with the same values, if its group has one (offered as its replacement). */
export function successorOf(item: Item | undefined, pg: Groups): Item | undefined {
  if (!item?.part_group_id || isCurrent(item)) return undefined;
  const group = pg.byId.get(item.part_group_id);
  if (!group) return undefined;
  const key = valuesKey(group.varies_by, valuesOf(item));
  return pg.membersByGroup.get(group.id)?.find((m) => m.id !== item.id && isCurrent(m) && valuesKey(group.varies_by, valuesOf(m)) === key);
}

type Choice = { row: FillRow; pick: string | null; on: boolean };

/** Fill from group… for one draft option line: the proposal as a table, then the map rows that change. */
export function FillDialog({ ix, bom, line, onClose }: { ix: BomIndex; bom: BomRow; line: BomLineRow; onClose: () => void }) {
  const { groups, byId, membersByGroup } = usePartGroups();
  const maps = useRows("bom_option_map");
  const own: OwnMap[] = useMemo(() => maps.filter((m) => m.bom_line_id === line.id), [maps, line.id]);
  const picked = lineGroup(ix, line, (id) => (ix.itemById.get(id) as Item | undefined)?.part_group_id ?? null);
  // The line's group, else the groups that vary by its option (a new line with no part yet).
  const offered = groups.filter((g) => g.id === picked || g.varies_by.some((a) => a.toLowerCase() === line.option_name.toLowerCase()));
  const [groupId, setGroupId] = useState(picked ?? offered[0]?.id ?? groups[0]?.id ?? "");
  const group = byId.get(groupId);
  const members = (group && membersByGroup.get(group.id)) ?? [];
  const proposal = useMemo(
    () =>
      group
        ? fillProposal({ line, values: lineValues(ix, bom, line), group, members, own, elsewhere: mapsElsewhere(ix, line.option_name, line.id) })
        : { rows: [], axis: null, pins: {} },
    [group, members, ix, bom, line, own],
  );
  const [choices, setChoices] = useState<Record<string, { pick?: string | null; on?: boolean }>>({});
  const [busy, setBusy] = useState(false);
  const rows: Choice[] = proposal.rows.map((row) => {
    const c = choices[row.value] ?? {};
    const pick = c.pick !== undefined ? c.pick : row.proposed;
    return { row, pick, on: c.on ?? true };
  });
  const ownBy = new Map(own.map((m) => [m.option_value.toLowerCase(), m]));
  const writeOf = (c: Choice) => fillWrite(c.row.value, c.pick, line.component_item_id, ownBy.get(c.row.value.toLowerCase()));
  const writes = rows.filter((c) => c.on && writeOf(c));
  const toPick = rows.filter((c) => !c.pick).length;
  const sku = (id: string | null) => (id ? (ix.itemById.get(id)?.sku ?? "?") : "");
  const label = (id: string | null) => {
    const m = members.find((x) => x.id === id);
    return m && group ? memberLabel(group, m) : "";
  };

  const run = async () => {
    if (busy || !writes.length) return;
    setBusy(true);
    let done = 0;
    for (const c of writes) {
      const w = writeOf(c);
      if (!w) continue;
      const ok =
        w.kind === "create"
          ? await mutate("bom_option_map.create", { id: crypto.randomUUID(), bom_line_id: line.id, option_value: w.value, component_item_id: w.itemId })
          : w.kind === "update"
            ? await mutate("bom_option_map.update", { id: w.mapId, patch: { component_item_id: w.itemId } })
            : await mutate("bom_option_map.remove", { id: w.mapId });
      if (ok) done++;
    }
    setBusy(false);
    toast.ok(`${plural(done, "pick")} written on line ${line.position}`);
    onClose();
  };

  const pins = Object.entries(proposal.pins);
  return (
    <Modal
      title={`Fill line ${line.position} from a group`}
      width={680}
      onClose={onClose}
      onSubmit={run}
      footer={
        <>
          <span className="mr-auto text-[12px] text-muted">
            {writes.length ? `${plural(writes.length, "change")} to write` : "Nothing to change"}
            {toPick ? ` · ${toPick} to pick` : ""}
          </span>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" icon={<Wand2 className="size-3.5" />} disabled={busy || !writes.length} kbd="↵">
            Write {writes.length || ""}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-muted">Group</span>
          <Select
            value={groupId}
            onChange={(e) => {
              setGroupId(e.target.value);
              setChoices({});
            }}
            options={(offered.length ? offered : groups).map((g) => ({ value: g.id, label: g.name }))}
            className="w-64"
          />
          <span className="text-[12px] text-faint">
            {proposal.axis ? `Values matched by ${proposal.axis}` : `It doesn't vary by ${line.option_name}: from maps and the line's part`}
            {pins.length ? ` · ${pins.map(([a, v]) => `${a} = ${v}`).join(", ")} (from the line)` : ""}
          </span>
        </div>
        <div className="max-h-[50vh] overflow-y-auto rounded-md border border-line">
          <table className="w-full text-[12px]">
            <thead className="sticky top-0 bg-surface-2 text-muted">
              <tr>
                <th className="w-6 px-2 py-1" />
                <th className="px-2 py-1 text-left font-medium">{line.option_name}</th>
                <th className="px-2 py-1 text-left font-medium">Now</th>
                <th className="px-2 py-1 text-left font-medium">Gets</th>
                <th className="px-2 py-1 text-left font-medium">Why</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => {
                const w = writeOf(c);
                const options = [
                  ...(c.row.candidates.length ? c.row.candidates : members.filter(isCurrent).map((m) => m.id)),
                  ...(line.component_item_id && !c.row.candidates.includes(line.component_item_id) ? [line.component_item_id] : []),
                ];
                return (
                  <tr key={c.row.value} className={cx("border-t border-line", !c.pick && "bg-warn-soft/40")}>
                    <td className="px-2">
                      <input
                        type="checkbox"
                        checked={c.on && !!w}
                        disabled={!w}
                        onChange={(e) => setChoices((x) => ({ ...x, [c.row.value]: { ...x[c.row.value], on: e.target.checked } }))}
                        className="accent-[var(--accent)]"
                      />
                    </td>
                    <td className="px-2 py-1">{c.row.value}</td>
                    <td className="px-2 py-1 font-mono whitespace-nowrap text-muted">
                      {sku(c.row.now) || <span className="font-sans text-danger">none</span>}
                    </td>
                    <td className="px-1 py-0.5">
                      <Select
                        value={c.pick ?? ""}
                        onChange={(e) => setChoices((x) => ({ ...x, [c.row.value]: { ...x[c.row.value], pick: e.target.value || null, on: true } }))}
                        options={[
                          { value: "", label: "Pick one…" },
                          ...[...new Set(options)].map((id) => ({
                            value: id,
                            label: `${sku(id)}${label(id) ? ` · ${label(id)}` : ""}${id === line.component_item_id ? " (the line's part)" : ""}`,
                          })),
                        ]}
                        className={cx("h-7", !c.pick && "border-warn")}
                      />
                    </td>
                    <td className="px-2 py-1">
                      {!c.pick ? (
                        <Badge tone="warn">pick one</Badge>
                      ) : c.pick !== c.row.proposed ? (
                        <span className="text-muted">your pick</span>
                      ) : (
                        <span className="text-muted">{FILL_SOURCE_LABELS[c.row.source]}</span>
                      )}
                      {w && <span className="ml-1.5 font-medium text-accent-text">{w.kind === "remove" ? "· back to the line's part" : "· changes"}</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="text-[11px] text-faint">
          Writes this draft's option map only: a value that gets the line's own part needs no map row, so a line that uses one part for most colours stays as it
          is. Released revisions never change.
        </div>
      </div>
    </Modal>
  );
}
